import React from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";

import { RequestGoodsRentalResponse } from "../../../../types/goods";

import {
  formatDiffSecondToString,
  getDateRangeString,
  remainTime,
} from "../../../../libs/dateFormat";

import { RENT_REQUEST_TIMEOUT } from "../../../../types/rentTypes";

import VerticalRule from "../../../Rule/Vertical";
import CommonButton from "../../../Button";

import { ReportStatusRadioIcon } from "../../../../pages/Manager/Report";

type Props = {
  index: number;
  item: RequestGoodsRentalResponse;
  items: RequestGoodsRentalResponse[];
  onClickManualRental?: (item: RequestGoodsRentalResponse) => void;
  onClickManualReturn?: (item: RequestGoodsRentalResponse) => void;
  onClickRestore?: (item: RequestGoodsRentalResponse) => void;
  onClickPush?: (item: RequestGoodsRentalResponse) => void;
};

export default function ManagerRentalItem({
  index,
  item,
  items,
  onClickManualRental,
  onClickManualReturn,
  onClickRestore,
  onClickPush,
}: Props) {
  const navigate = useNavigate();

  const getRentStatus = (status: string) => {
    switch (status) {
      case "REQUESTED":
        return "REQUESTED";
      case "RENTED":
        return "PROGRESSED";
      case "COMPLETED":
      case "DONE":
        return "COMPLETED";
      case "CANCELED":
        return "CANCELED";
      case "LOST_DAMAGED":
      case "OVERDUE":
        return "ERRORED";
      default:
        return "DEFAULT";
    }
  };

  const getRentStatusLabel = (status: string) => {
    switch (status) {
      case "REQUESTED":
        return "신청";
      case "RENTED":
        return "대여중";
      case "OVERDUE":
        return "연체";
      case "COMPLETED":
        return "반납완료";
      case "LOST_DAMAGED":
        return "분실손상";
      case "DONE":
        return "반납완료";
      case "CANCELED":
        return "취소";
      default:
        return "DEFAULT";
    }
  };

  const getRemainTime = (item: RequestGoodsRentalResponse) => {
    switch (item.rentalServiceStatus) {
      case "REQUESTED":
        return `신청 예약 ${formatDiffSecondToString(
          remainTime(item.createdAt, RENT_REQUEST_TIMEOUT)
        )} 남음`;
      case "RENTED":
        return `${getDateRangeString(
          item.rentalAt ?? "",
          item.returnDueAt ?? ""
        )} (${formatDiffSecondToString(
          remainTime(item.returnDueAt ?? "", 0)
        )} 남음)`;
      case "DONE":
      case "COMPLETED":
        return `${getDateRangeString(
          item.rentalAt ?? "",
          item.actualReturnAt ?? ""
        )}`;
      case "LOST_DAMAGED":
        return `${getDateRangeString(
          item.rentalAt ?? "",
          item.lostDamagedAt ?? ""
        )}`;
      case "OVERDUE":
        return (
          <>
            {`${getDateRangeString(
              item.rentalAt ?? "",
              item.returnDueAt ?? ""
            )} `}
            <span
              className="text-brand-primary-red"
              data-testid="span-timeout"
            >{`(${formatDiffSecondToString(
              Math.abs(remainTime(item.returnDueAt ?? "", 0))
            )} 초과)`}</span>
          </>
        );
      default:
        return null;
    }
  };

  const getActionButtons = (item: RequestGoodsRentalResponse) => {
    switch (item.rentalServiceStatus) {
      case "REQUESTED":
        return (
          <CommonButton
            variant="tertiary-s"
            className="flex-1 h-8 whitespace-nowrap"
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              onClickManualRental?.(item);
            }}
          >
            수동 대여
          </CommonButton>
        );
      case "RENTED":
        return (
          <CommonButton
            variant="tertiary-s"
            className="flex-1 h-8 whitespace-nowrap"
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              onClickManualReturn?.(item);
            }}
          >
            수동 반납
          </CommonButton>
        );
      case "LOST_DAMAGED":
        return (
          <CommonButton
            variant="tertiary-s"
            className="flex-1 h-8 whitespace-nowrap"
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              onClickRestore?.(item);
            }}
          >
            정상 반납 처리
          </CommonButton>
        );
      case "OVERDUE":
        return (
          <React.Fragment>
            <CommonButton
              variant="tertiary-s"
              className="flex-1 h-8 whitespace-nowrap"
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                onClickManualReturn?.(item);
              }}
            >
              수동 반납
            </CommonButton>
            <CommonButton
              variant="tertiary-s"
              className="flex-1 h-8 whitespace-nowrap"
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                onClickPush?.(item);
              }}
            >
              반납알림 발송
            </CommonButton>
          </React.Fragment>
        );
      default:
        return null;
    }
  };

  return (
    <React.Fragment key={index}>
      <div className="flex gap-2.5 relative">
        <div className="h-full w-[24px] flex justify-center absolute z-0">
          {items.length > 1 && (
            <VerticalRule
              className={clsx([
                "!h-full !bg-[#d9d9d9]",
                {
                  "!h-[calc(100%-24px)] ": index === 0,
                  "!h-[24px]": index === items.length - 1,
                },
              ])}
              wrapperClassName={clsx([
                {
                  "!items-end": index === 0,
                  "!items-start": index === items.length - 1,
                },
              ])}
            />
          )}
        </div>
        <div className="w-[24px] pt-[18px] z-10">
          <ReportStatusRadioIcon
            status={getRentStatus(item.rentalServiceStatus)}
          />
        </div>
        <div className="flex flex-col gap-1 pt-[18px]">
          <div
            className={clsx([
              "text-base font-bold",
              {
                " text-brand-primary-red": ["OVERDUE", "LOST_DAMAGED"].includes(
                  item.rentalServiceStatus
                ),
              },
            ])}
            data-testid="status-text"
          >
            {getRentStatusLabel(item.rentalServiceStatus)}
          </div>
          <div className=" text-xs text-[#9f9f9f]">
            {item.createdAt
              .split("/")
              .slice(1, 3)
              .join("/")
              .split(":")
              .slice(0, 2)
              .join(":")}
          </div>
        </div>
        <div
          className={clsx([
            "p-[15px] border border-brand-neutral-10 rounded-[10px] flex flex-col gap-2.5 flex-1 h-fit max-w-[calc(100vw-148px)]",
            {
              "bg-brand-neutral-05": item.rentalServiceStatus === "CANCELED",
            },
          ])}
          onClick={() =>
            navigate(`/manager/rental-manager/${item.goodsServiceId}`)
          }
        >
          <div className="flex gap-2.5 items-center">
            <div
              className={clsx(["flex-1 font-medium leading-[19px] truncate"])}
              data-testid="text-goods-name"
            >
              {item.goodsName}
            </div>
            <ManagerRentalRightArrow />
          </div>
          <div className="flex flex-col gap-2.5">
            <div className="text-xs text-brand-neutral-50 flex flex-col gap-1.5">
              <div className="flex gap-1" data-testid="text-rental-place">
                <ManagerRentalLocationIcon />
                {!["DONE", "COMPLETED"].includes(item.rentalServiceStatus)
                  ? `${item.rentalBuildingName} ${item.rentalStoreName}`
                  : `${item.returnBuildingName} ${item.returnStoreName}`}
              </div>
              <div className="flex gap-1" data-testid="text-username">
                <ManagerRentalPersonIcon />
                {item.createdByName}
              </div>
              {item.rentalServiceStatus !== "CANCELED" && (
                <div className="flex gap-1" data-testid="text-remaining-time">
                  <div className="min-w-4">
                    <ManagerRentalCalendarIcon />
                  </div>
                  <span className="flex flex-col">{getRemainTime(item)}</span>
                </div>
              )}
            </div>
            {!["DONE", "COMPLETED", "CANCELED"].includes(
              item.rentalServiceStatus
            ) && (
              <div
                className="flex gap-1 items-center design_min:flex-wrap"
                data-testid="action-button-group"
              >
                {getActionButtons(item)}
              </div>
            )}
          </div>
        </div>
      </div>
      {index < items.length - 1 && (
        <div className="h-5 w-[24px] flex justify-center">
          <VerticalRule className="!h-full !bg-[#d9d9d9]" />
        </div>
      )}
    </React.Fragment>
  );
}

export const ManagerRentalPersonIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.5731 4.626C10.5731 6.05808 9.4116 7.21879 7.97858 7.21879C6.54556 7.21879 5.38409 6.05808 5.38409 4.626C5.38409 3.19391 6.54556 2.0332 7.97858 2.0332C9.4116 2.0332 10.5731 3.19391 10.5731 4.626Z"
      fill="#B7B7B8"
      stroke="#B7B7B8"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.8143 13.2916H3.14282C3.14282 13.2623 3.14282 13.4496 3.14282 12.858C3.14282 11.2556 4.44185 9.95703 6.04427 9.95703H9.91287C11.5153 9.95703 12.8143 11.2556 12.8143 12.858C12.8143 13.4496 12.8143 13.2623 12.8143 13.2916Z"
      fill="#B7B7B8"
      stroke="#B7B7B8"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
  </svg>
);

export const ManagerRentalRightArrow = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.2">
      <path
        d="M9 18.5L15 12.5L9 6.5"
        stroke="black"
        strokeWidth="1.75"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const ManagerRentalCalendarIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="2.49988"
      y="3.32227"
      width="11"
      height="10"
      rx="1"
      fill="#B7B7B8"
      stroke="#B7B7B8"
      strokeWidth="1.23"
    />
    <line
      x1="5.61488"
      y1="1.32227"
      x2="5.61488"
      y2="5.32227"
      stroke="white"
      strokeWidth="1.23"
    />
    <line
      x1="10.6149"
      y1="1.32227"
      x2="10.6149"
      y2="5.32227"
      stroke="white"
      strokeWidth="1.23"
    />
    <line
      x1="5.61488"
      y1="2.59203e-08"
      x2="5.61488"
      y2="3"
      stroke="#B7B7B8"
      strokeWidth="1.23"
    />
    <line
      x1="10.6149"
      y1="2.59203e-08"
      x2="10.6149"
      y2="3"
      stroke="#B7B7B8"
      strokeWidth="1.23"
    />
    <line x1="2.99988" y1="7.02246" x2="12.9999" y2="7.02246" stroke="white" />
  </svg>
);

export const ManagerRentalLocationIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.97849 14.0273L4.13514 10.3126C3.39363 9.58116 2.89205 8.65438 2.69331 7.64849C2.49457 6.64261 2.6075 5.6024 3.01795 4.6583C3.42841 3.71421 4.1181 2.90825 5.00057 2.34148C5.88303 1.77472 6.91897 1.47238 7.9785 1.47238C9.03802 1.47238 10.074 1.77472 10.9564 2.34148C11.8389 2.90825 12.5286 3.71421 12.939 4.65831C13.3495 5.6024 13.4624 6.64261 13.2637 7.64849C13.0649 8.65438 12.5634 9.58116 11.8218 10.3126L7.97849 14.0273Z"
      fill="#B7B7B8"
      stroke="#B7B7B8"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.1744 6.93431C10.1744 8.14714 9.19121 9.13033 7.97837 9.13033C6.76554 9.13033 5.78235 8.14714 5.78235 6.93431C5.78235 5.72148 6.76554 4.73828 7.97837 4.73828C9.19121 4.73828 10.1744 5.72148 10.1744 6.93431Z"
      fill="white"
      stroke="white"
      strokeWidth="1.75"
    />
  </svg>
);
